import React, { useState } from 'react';
import StudentInput from './StudentInput';
import ClothingSelector from './ClothingSelector';
import PriceSummary from './PriceSummary';
import { submitOrder } from '../services/api';

function OrderForm({ config }) {
  const [studentData, setStudentData] = useState({
    vorname: '',
    nachname: '',
    klasse: '',
    elternname: '',
    email: '',
    matchedStudent: null,
    alternatives: [],
  });

  const [clothingItems, setClothingItems] = useState({});

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    // Pflichtfelder Schülerdaten
    if (!studentData.vorname.trim()) {
      newErrors.vorname = 'Vorname ist erforderlich';
    }
    if (!studentData.nachname.trim()) {
      newErrors.nachname = 'Nachname ist erforderlich';
    }
    if (!studentData.klasse) {
      newErrors.klasse = 'Bitte wählen Sie eine Klasse';
    }
    if (!studentData.elternname.trim()) {
      newErrors.elternname = 'Name der Eltern ist erforderlich';
    }

    // E-Mail Validierung
    if (!studentData.email.trim()) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studentData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    // Mindestens ein Artikel ausgewählt?
    const hasItems = Object.values(clothingItems).some((qty) => qty > 0);
    if (!hasItems) {
      newErrors.items = 'Bitte wählen Sie mindestens einen Artikel aus';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    const artikel = config?.artikel || {};
    const set = config?.set || {};
    const rabattAktiv = config?.rabatt_aktiv ?? true;

    // Anzahl pro Artikel summieren
    const counts = {};
    for (const [key, art] of Object.entries(artikel)) {
      if (!art.groessen || art.groessen.length === 0) {
        counts[key] = clothingItems[`${key}_anzahl`] || 0;
      } else {
        counts[key] = art.groessen.reduce((sum, g) => sum + (clothingItems[`${key}_${g}`] || 0), 0);
      }
    }

    let total = 0;

    if (rabattAktiv) {
      const setArtikel = Object.entries(artikel).filter(([, a]) => a.set_rabatt);
      const setCounts = setArtikel.map(([key]) => counts[key] || 0);
      const autoSetCount = setCounts.length > 0 ? Math.min(...setCounts) : 0;

      total += autoSetCount * (set.preis || 0);
      for (const [key, art] of Object.entries(artikel)) {
        const einzeln = art.set_rabatt ? counts[key] - autoSetCount : counts[key];
        total += einzeln * art.preis;
      }
    } else {
      for (const [key, art] of Object.entries(artikel)) {
        total += counts[key] * art.preis;
      }
    }

    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      // Scroll zu erstem Fehler
      const firstError = document.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        // Schülerdaten (nur IDs, keine Namen von anderen Schülern)
        schueler_vorname: studentData.vorname,
        schueler_nachname: studentData.nachname,
        schueler_id: studentData.matchedStudent?.schueler_id || '',
        match_score: studentData.matchedStudent?.score || 0,
        alternatives: (studentData.alternatives || []).map(a => ({
          schueler_id: a.schueler_id,
          score: a.score
        })),
        klasse: studentData.klasse,
        elternname: studentData.elternname,
        email: studentData.email,
        gesamtbetrag: calculateTotal(),
      };

      // Artikel-Mengen dynamisch aus Config aufbauen
      for (const [key, art] of Object.entries(config?.artikel || {})) {
        if (!art.groessen || art.groessen.length === 0) {
          orderData[`${key}_anzahl`] = clothingItems[`${key}_anzahl`] || 0;
        } else {
          for (const g of art.groessen) {
            orderData[`${key}_${g}`] = clothingItems[`${key}_${g}`] || 0;
          }
        }
      }

      await submitOrder(orderData);
      setSubmitSuccess(true);

      // Scroll nach oben
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Submit error:', err);
      // Error message is now already extracted in api.js
      setSubmitError(err.message || 'Bestellung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  // Erfolgsseite
  if (submitSuccess) {
    return (
      <div className="alert alert-success" role="alert">
        <h4 className="alert-heading">Bestellung erfolgreich!</h4>
        <p>
          Ihre Bestellung für <strong>{studentData.vorname} {studentData.nachname}</strong> wurde erfolgreich übermittelt.
        </p>
        <p>
          <strong>Gesamtbetrag:</strong>{' '}
          {calculateTotal().toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
          })}
        </p>
        <hr />
        <p className="mb-0">
          Eine Bestätigung wurde an <strong>{studentData.email}</strong> gesendet.
        </p>
        <hr />
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Neue Bestellung
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      {/* Info-Box */}
      <div className="card mb-4 bg-light">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <strong>Schuljahr:</strong> {config?.schuljahr}
            </div>
            <div className="col-md-6">
              <strong>Bestelldatum:</strong>{' '}
              {(() => {
                const today = new Date();
                const day = String(today.getDate()).padStart(2, '0');
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const year = today.getFullYear();
                return `${day}.${month}.${year}`;
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Schülerdaten mit Matching */}
      <StudentInput
        data={studentData}
        onChange={(update) => {
          if (typeof update === 'function') {
            setStudentData(update);
          } else {
            setStudentData(update);
          }
        }}
        errors={errors}
        setErrors={setErrors}
        klassen={config?.klassen}
      />

      {/* Artikelauswahl */}
      <ClothingSelector
        items={clothingItems}
        onChange={setClothingItems}
        artikel={config?.artikel}
        rabattAktiv={config?.rabatt_aktiv ?? true}
      />

      {/* Fehler bei Artikelauswahl */}
      {errors.items && (
        <div className="alert alert-danger" role="alert">
          {errors.items}
        </div>
      )}

      {/* Preiszusammenfassung */}
      <PriceSummary
        items={clothingItems}
        artikel={config?.artikel}
        set={config?.set}
        rabattAktiv={config?.rabatt_aktiv ?? true}
      />

      {/* Submit Button */}
      <div className="d-grid gap-2">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Wird gesendet...</span>
              </span>
              Wird gesendet...
            </>
          ) : (
            <>
              Bestellung absenden
              {calculateTotal() > 0 && (
                <span className="ms-2">
                  ({calculateTotal().toLocaleString('de-DE', {
                    style: 'currency',
                    currency: 'EUR',
                  })})
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default OrderForm;
